/**
 * Jyotish Tarang -- i18next configuration.
 *
 * Phase 1 (14 Jul 2026, LANGUAGE_SUPPORT_PLAN.md): infrastructure only.
 * English is the only language with real translated content; the other 18
 * resource bundles are intentionally empty {} stub files (see
 * frontend/src/locales/<lang>/translation.json) so the loading/switching
 * machinery is proven end-to-end now, with real per-language content filled
 * in language-by-language starting Phase 2 (Hindi). `fallbackLng: 'en'`
 * means any key missing from a stub bundle renders in English rather than
 * showing a raw i18next key -- so selecting a not-yet-translated language
 * never breaks the UI, it just falls back silently per-string.
 *
 * Not using i18next-http-backend / lazy-loading here: 19 small JSON files
 * bundled directly is simpler and avoids extra network waterfalls for a
 * CRA app of this size. Revisit if the per-language bundles grow large
 * enough that bundle size becomes a real concern.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { LANGUAGE_CODES, DEFAULT_LANGUAGE } from './languages';

import en from './locales/en/translation.json';
import hi from './locales/hi/translation.json';
import as_ from './locales/as/translation.json';
import bn from './locales/bn/translation.json';
import zh from './locales/zh/translation.json';
import fr from './locales/fr/translation.json';
import de from './locales/de/translation.json';
import gu from './locales/gu/translation.json';
import it from './locales/it/translation.json';
import ja from './locales/ja/translation.json';
import kn from './locales/kn/translation.json';
import ml from './locales/ml/translation.json';
import mr from './locales/mr/translation.json';
import or_ from './locales/or/translation.json';
import pt from './locales/pt/translation.json';
import pa from './locales/pa/translation.json';
import es from './locales/es/translation.json';
import ta from './locales/ta/translation.json';
import te from './locales/te/translation.json';

const resources = {
  en: { translation: en },
  hi: { translation: hi },
  as: { translation: as_ },
  bn: { translation: bn },
  zh: { translation: zh },
  fr: { translation: fr },
  de: { translation: de },
  gu: { translation: gu },
  it: { translation: it },
  ja: { translation: ja },
  kn: { translation: kn },
  ml: { translation: ml },
  mr: { translation: mr },
  or: { translation: or_ },
  pt: { translation: pt },
  pa: { translation: pa },
  es: { translation: es },
  ta: { translation: ta },
  te: { translation: te },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: LANGUAGE_CODES,
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      // 16 Aug 2026: dropped 'navigator' from the detection order -- product
      // decision that the app should default to English for anyone without
      // an explicit saved preference, rather than auto-detecting from the
      // browser's Accept-Language/navigator.language. Only an explicit
      // choice (this localStorage cache, or the backend's `users.ui_language`
      // synced in App.tsx after /api/auth/me loads) should switch the UI
      // away from English. With no localStorage entry, LanguageDetector
      // finds nothing and i18next falls through to fallbackLng (DEFAULT_LANGUAGE
      // = 'en' per languages.ts), so the effective default is English.
      order: ['localStorage'],
      lookupLocalStorage: 'jt_ui_language',
      caches: ['localStorage'],
    },
  });

export default i18n;
